import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/policy")({
	component: Policy
});

function Policy() {
	return (
		<>
			<section className="space-y-4">
				<div className=" w-full bg-white rounded-lg p-6 text-sm overflow-y-auto">
					<h2 className="text-xl font-bold mb-4">INTASIDA DATA PROTECTION / PRIVACY POLICY </h2>
					<ol className="list-decimal pl-6 space-y-4">
						<li>
							<h3 className="font-semibold">Introduction</h3>
							<p>
								Intasida Limited ("Intasida," "Company," "we," "us," or "our") is committed to protecting the rights and freedoms of data subjects (customers, employees, and partners) and ensuring the safe and secure processing of their personal data in compliance with the Nigeria Data Protection Act 2023, the Central Bank of Nigeria (CBN) Guidelines on Data Protection for Financial Institutions, and other applicable financial regulatory frameworks.
							</p>
							<p>
								We process personal data to provide secure and efficient financial services, ensure regulatory compliance, prevent fraud, and enhance user experience. This policy outlines our data protection commitments and ensures our employees and partners understand the principles governing the use of personal data.
							</p>
						</li>
						<li>
							<h3 className="font-semibold">Definitions</h3>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Personal Data: Any information that identifies an individual, including name, phone number, email, financial details, account numbers, and transaction history.
								</li>
								<li>
									Special Category Data: Sensitive data including biometric data, health information, and criminal records, which require enhanced protection.
								</li>
								<li>
									Data Controller: Intasida Limited, determining the purpose and means of data processing.
								</li>
								<li>
									Data Processor: Any third party processing personal data on behalf of Intasida.
								</li>
								<li>
									Processing: Any operation performed on personal data, including collection, storage, transmission, and deletion.
								</li>
								<li>
									Supervisory Authority: The National Data Protection Commission (NDPC) and other relevant financial regulatory bodies.
								</li>
							</ul>
						</li>
						<li>
							<h3 className="font-semibold">Scope</h3>
							<p>
								This policy applies to all personal data processed by Intasida Limited, including data collected from customers, employees, vendors, and third-party service providers.
							</p>
						</li>
						<li>
							<h3 className="font-semibold">Purpose</h3>
							<p>
								Intasida processes personal data for:
							</p>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Marketing and customer engagement (with consent)
								</li>
								<li>
									Financial transactions, including payments and loan services
								</li>
								<li>
									Fraud detection and compliance with anti-money laundering (AML) regulations
								</li>
								<li>
									Customer support and account management
								</li>
								<li>
									Internal business operations and human resources management
								</li>
							</ul>
						</li>
						<li>
							<h3 className="font-semibold">Our Procedures</h3>
							<h4 className="font-semibold">Lawful Bases for Processing</h4>
							<p>
								Processing of personal data shall be lawful, fair, and transparent. We rely on the following lawful bases:
							</p>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Consent: Where customers explicitly agree to data collection and processing.
								</li>
								<li>
									Contractual Necessity: When processing is essential to fulfill a financial service.
								</li>
								<li>
									Legal Obligation: To comply with regulatory requirements.
								</li>
								<li>
									Legitimate Interest: When processing is necessary for fraud prevention, security, and business operations.
								</li>

							</ul>
							<h4 className="font-semibold">Security Measures</h4>
							<p>
								Intasida implements robust security measures, including:
							</p>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Encryption of sensitive financial data.
								</li>
								<li>
									Multi-factor authentication (MFA) for customer accounts.
								</li>
								<li>
									Regular security audits and risk assessments.
								</li>
								<li>
									Employee training on data security and fraud prevention.
								</li>

							</ul>

							<h4 className="font-semibold">Data Minimization & Retention</h4>
							<p>
								We collect only necessary data and retain personal information for the duration required by law. Customer financial records are retained in line with the Nigerian Data Protection Act 2023 and other relevant regulations.
							</p>

						</li>

						<li>
							<h3 className="font-semibold">Responsibilities of Intasida</h3>
							<p>
								Intasida shall ensure that personal data is:
							</p>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Processed in a fair, lawful and transparent manner.
								</li>
								<li>
									Collected for specified, explicit and legitimate purposes, and not to be further processed in a way incompatible with this purposes.
								</li>
								<li>
									Adequate, relevant and limited to the minimum necessary for the purposes for which the personal data was collected or further processed.
								</li>
								<li>
									Retained for not longer than is necessary to achieve the lawful bases for which the personal data was collected or further processed.
								</li>
								<li>
									Accurate, complete, not misleading, and, where necessary kept up to date having regard to the purposes for which the personal data is collected or is further processed.
									{" "}
								</li>

							</ul>
						</li>
						<li>
							<h3 className="font-semibold">Rights of Individuals</h3>
							<p>
								Data subjects have the following rights:
							</p>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Right to Access: Request a copy of personal data processed by Intasida.
								</li>
								<li>
									Right to Rectification: Correct inaccurate personal data.
								</li>
								<li>
									Right to Erasure: Request deletion of data where no longer necessary.
								</li>
								<li>
									Right to Restriction: Limit processing in certain conditions.
								</li>
								<li>
									Right to Data Portability: Obtain and reuse data for personal purposes.
								</li>
								<li>
									Right to Object: Stop processing based on legitimate interests.
								</li>

							</ul>
						</li>
						<li>
							<h3 className="font-semibold">Third-Party Processing</h3>
							<p>
								Intasida engages third-party service providers (e.g., payment processors, KYC verification services) under strict data processing agreements to ensure compliance with this policy and the Nigerian Data Protection Act 2023.
							</p>
						</li>
						<li>
							<h3 className="font-semibold">Reporting Breaches</h3>
							<p>
								In case of a data breach, Intasida will:
							</p>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Notify affected individuals and the NDPC within 72 hours.
								</li>
								<li>
									Investigate and mitigate risks associated with the breach.
								</li>
								<li>
									Take corrective actions to prevent future occurrences.
								</li>
							</ul>
						</li>
						<li>
							<h3 className="font-semibold">Complaince and Audits</h3>
							<p>
								Intasida is committed to maintaining the highest standards of data protection and ensuring full compliance with the Nigerian Data Protection Act 2023. To this end, the company conducts periodic audits and assessments to evaluate its data processing activities, security measures, and overall adherence to regulatory requirements. These audits are designed to:
							</p>
							<ul className="list-disc pl-6 space-y-2">
								<li>
									Identify and mitigate potential risks associated with data handling and storage.
								</li>
								<li>
									Ensure that all data protection policies and procedures align with legal and industry standards.
								</li>
								<li>
									Assess the effectiveness of technical and organizational measures implemented to safeguard personal data.
								</li>
								<li>
									Verify compliance with data subject rights, including access, rectification, and erasure requests.
								</li>
								<li>
									Review third-party processors and service providers to confirm adherence to contractual and regulatory obligations.
								</li>

							</ul>
							<p>
								Any identified non-compliance issues are promptly addressed through corrective measures, and employees are regularly trained on best practices to uphold data protection principles. Intasida remains proactive in updating its policies and processes in response to evolving data protection laws and industry developments.

							</p>
						</li>
					</ol>
				</div>
			</section>
			<section className="space-y-4">

			</section>
		</>

	);
}
